class PDPStickyBar extends HTMLElement {
  constructor() {
    super();
    this.currentVariant = null;
    this.variantSelector = null;
    this.ticking = false;
    this.scrollThreshold = 200;

    this.boundOnScroll = this.onScroll.bind(this);
    this.boundVariantSelected = this.handleVariantSelected.bind(this);
    this.boundVariantInputChange = this.handleVariantInputChange.bind(this);
    this.boundAddToCart = this.handleAddToCart.bind(this);
    this.boundCartStateSync = this.syncBodyScrollState.bind(this);
    this.boundStayAiPriceUpdate = this.handleStayAiPriceUpdate.bind(this);
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.boundOnScroll, { passive: true });
    window.removeEventListener('resize', this.boundOnScroll, { passive: true });
    document.removeEventListener('vf:stayai-price-updated', this.boundStayAiPriceUpdate);

    if (this.variantSelector) {
      this.variantSelector.removeEventListener('variant-selected', this.boundVariantSelected);
    }

    document.querySelectorAll('input[name="variant"]').forEach((input) => {
      input.removeEventListener('change', this.boundVariantInputChange);
    });

    const addToCartButton = this.querySelector('.add-to-cart-btn');
    if (addToCartButton) {
      addToCartButton.removeEventListener('click', this.boundAddToCart);
    }

    if (this.variantSelectorObserver) {
      this.variantSelectorObserver.disconnect();
    }

    if (this.rebuyCartObserver) {
      this.rebuyCartObserver.disconnect();
    }
  }

  init() {
    if (!this.isConnected) {
      setTimeout(() => this.init(), 100);
      return;
    }

    this.buyboxElement = document.querySelector('.lsg-buybox, .lsg-buybox__content');
    this.footerElement = document.querySelector('footer');

    this.updateStickyBar();

    window.addEventListener('scroll', this.boundOnScroll, { passive: true });
    window.addEventListener('resize', this.boundOnScroll, { passive: true });

    // Stay AI price sync
    document.addEventListener('vf:stayai-price-updated', this.boundStayAiPriceUpdate);

    this.setupVariantSelectorListener();

    document.querySelectorAll('input[name="variant"]').forEach((input) => {
      input.addEventListener('change', this.boundVariantInputChange);
    });

    const addToCartButton = this.querySelector('.add-to-cart-btn');
    if (addToCartButton) {
      addToCartButton.addEventListener('click', this.boundAddToCart);
    }

    this.observeVariantSelector();
    this.observeRebuyCart();
    this.syncBodyScrollState();
  }

  setupVariantSelectorListener() {
    if (this.variantSelector) {
      this.variantSelector.removeEventListener('variant-selected', this.boundVariantSelected);
    }

    this.variantSelector = document.querySelector('product-variant-selector');
    if (this.variantSelector) {
      this.variantSelector.addEventListener('variant-selected', this.boundVariantSelected);

      if (this.variantSelector.selectedVariantId) {
        const variants = JSON.parse(this.variantSelector.getAttribute('data-variants') || '[]');
        this.currentVariant = variants.find((v) => String(v.id) === String(this.variantSelector.selectedVariantId));
        if (this.currentVariant) {
          this.updateStickyBarVariant(this.currentVariant);
        }
      }
    }
  }

  observeVariantSelector() {
    const buyboxContainer = document.querySelector('.lsg-buybox__product-price-quantity-container, .lsg-buybox');
    if (!buyboxContainer) return;

    const observer = new MutationObserver(() => {
      const newVariantSelector = document.querySelector('product-variant-selector');
      if (newVariantSelector && newVariantSelector !== this.variantSelector) {
        this.setupVariantSelectorListener();
      }
    });

    observer.observe(buyboxContainer, { childList: true, subtree: true });
    this.variantSelectorObserver = observer;
  }

  observeRebuyCart() {
    const attachObserver = () => {
      const rebuyCart =
        document.querySelector('#rebuy-cart') ||
        document.querySelector('[data-rebuy-cart-layout]') ||
        document.querySelector('.rebuy-cart');

      if (!rebuyCart) {
        setTimeout(attachObserver, 300);
        return;
      }

      const update = () => {
        const isHidden =
          rebuyCart.getAttribute('aria-hidden') === 'true' ||
          rebuyCart.classList.contains('hidden') ||
          rebuyCart.style.display === 'none';

        this.style.display = isHidden ? '' : 'none';
        this.syncBodyScrollState();
      };

      update();

      const observer = new MutationObserver(update);
      observer.observe(rebuyCart, {
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-hidden']
      });

      this.rebuyCartObserver = observer;

      document.addEventListener('click', (e) => {
        if (
          e.target.closest('.rebuy-cart__flyout-close') ||
          e.target.closest('#rebuy-cart-close') ||
          e.target.closest('[aria-label="Close Cart"]')
        ) {
          setTimeout(() => this.syncBodyScrollState(), 50);
          setTimeout(() => this.syncBodyScrollState(), 250);
        }
      });
    };

    attachObserver();
  }

  syncBodyScrollState() {
    const rebuyCart =
      document.querySelector('#rebuy-cart') ||
      document.querySelector('[data-rebuy-cart-layout]') ||
      document.querySelector('.rebuy-cart');

    if (!rebuyCart) {
      document.body.classList.remove('overflow-hidden');
      document.body.style.removeProperty('overflow');
      return;
    }

    const isOpen =
      rebuyCart.getAttribute('aria-hidden') !== 'true' &&
      !rebuyCart.classList.contains('hidden') &&
      rebuyCart.style.display !== 'none';

    if (!isOpen) {
      document.body.classList.remove('overflow-hidden');
      document.documentElement.classList.remove('overflow-hidden');
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
    }
  }

  onScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => this.updateStickyBar());
      this.ticking = true;
    }
  }

  updateStickyBar() {
    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;
    let shouldShow = false;

    if (this.buyboxElement) {
      const buyboxRect = this.buyboxElement.getBoundingClientRect();
      if (buyboxRect.bottom < 0) shouldShow = true;
    } else if (scrollY > this.scrollThreshold) {
      shouldShow = true;
    }

    if (this.footerElement && shouldShow) {
      const footerRect = this.footerElement.getBoundingClientRect();
      if (footerRect.top <= viewportHeight) shouldShow = false;
    }

    this.classList.toggle('show', shouldShow);
    this.ticking = false;
  }

  handleVariantSelected(e) {
    this.currentVariant = e.detail.variant;
    this.updateStickyBarVariant(this.currentVariant);
  }

  handleVariantInputChange(e) {
    if (e.target.checked) {
      this.updateStickyBarFromVariantInput(e.target.value);
    }
  }

  // Stay AI price update handler — fires from vf:stayai-price-updated in script-product.js
  handleStayAiPriceUpdate(e) {
    const { price, isSubscription } = e.detail;
    const priceEl = document.getElementById('pdp-sticky-bar-price');
    if (!priceEl) return;

    if (isSubscription) {
      // Show subscription (discounted) price with base price as strikethrough
      const compareFormatted = this.currentVariant
        ? this.formatMoney(this.currentVariant.price)
        : null;

      priceEl.innerHTML = `
        <div class="lsg-buybox-product-price flex items-center gap-2">
          ${compareFormatted ? `
            <div class="lsg-buybox__compare-at-price text-primary-dark line-through text-[14px]">
              ${compareFormatted}
            </div>
          ` : ''}
          <div class="text-primary-dark font-bold text-[14px]">${price}</div>
        </div>
      `;
    } else {
      // One-time purchase — restore full variant price display
      if (this.currentVariant) {
        this.updateStickyBarVariant(this.currentVariant);
      } else {
        priceEl.innerHTML = `
          <div class="lsg-buybox-product-price flex items-center gap-2">
            <div class="text-primary-dark font-bold text-[14px]">${price}</div>
          </div>
        `;
      }
    }
  }

  handleAddToCart(e) {
    e.preventDefault();

    const variantInput =
      document.querySelector('input[name="variant"]:checked') ||
      document.querySelector('input[name="variant"]');

    const variantId = this.currentVariant?.id || variantInput?.value;

    if (!variantId) {
      alert('Please select a variant');
      return;
    }

    const quantityInput = document.querySelector('.product-qty, .qty-input');
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    // Delegate selling plan resolution to the variant selector which handles Stay AI correctly
    let sellingPlan = null;
    const variantSelectorEl = document.querySelector('product-variant-selector');
    if (variantSelectorEl && typeof variantSelectorEl.getSelectedSellingPlanId === 'function') {
      const raw = variantSelectorEl.getSelectedSellingPlanId();
      sellingPlan = raw != null && !Number.isNaN(Number(raw)) ? Number(raw) : null;
    } else {
      // Fallback: check standard and Stay AI selling plan inputs
      const sellingPlanInput =
        document.querySelector('input[name="selling_plan"]:checked') ||
        document.querySelector('input[name="selling_plan"]') ||
        document.querySelector('select[name="selling_plan"]') ||
        document.querySelector('select[name="stayai_selling_plan"]');

      if (sellingPlanInput) {
        const raw = sellingPlanInput.value;
        if (raw != null && raw !== '' && !Number.isNaN(Number(raw))) {
          sellingPlan = Number(raw);
        }
      }
    }

    this.addItemToCart(variantId, quantity, sellingPlan);
  }

  addItemToCart(variantId, quantity, sellingPlan) {
    if (window.CartApi?.addItemToCart) {
      window.CartApi.addItemToCart(variantId, quantity, sellingPlan);
      setTimeout(() => this.syncBodyScrollState(), 250);
      return;
    }

    const payload = {
      id: Number(variantId),
      quantity: Number(quantity)
    };

    if (sellingPlan != null) {
      payload.selling_plan = Number(sellingPlan);
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    })
      .then((r) => {
        if (!r.ok) throw new Error('Could not add item to cart');
        return r.json();
      })
      .then(() => {
        if (window.Rebuy?.SmartCart?.show) {
          window.Rebuy.SmartCart.show();
        } else if (window.Rebuy?.Cart?.show) {
          window.Rebuy.Cart.show();
        }

        setTimeout(() => this.syncBodyScrollState(), 250);
      })
      .catch((err) => {
        console.error('[pdp-sticky-bar] ATC error:', err);
        alert('Error adding item to cart. Please try again.');
      });
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      const moneyFormat =
        window.theme?.moneyFormat ||
        document.querySelector('[data-money-format]')?.dataset.moneyFormat ||
        '${{amount}}';
      return Shopify.formatMoney(cents, moneyFormat);
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  updateStickyBarVariant(variant) {
    if (!variant) return;

    this.querySelectorAll('#pdp-sticky-bar-variant-name').forEach((el) => {
      const isMobile = el.textContent.trimStart().startsWith('-');
      el.textContent = isMobile ? ` - ${variant.title}` : variant.title;
    });

    const priceEl = document.getElementById('pdp-sticky-bar-price');
    if (priceEl) {
      const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
      const discountPct = hasDiscount
        ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
        : 0;

      priceEl.innerHTML = `
        <div class="lsg-buybox-product-price flex items-center gap-2">
          ${hasDiscount ? `
            <div class="lsg-buybox__compare-at-price text-primary-dark line-through text-[14px]">
              ${this.formatMoney(variant.compare_at_price)}
            </div>
          ` : ''}
          <div class="text-primary-dark font-bold text-[14px]">
            ${this.formatMoney(variant.price)}
          </div>
          ${hasDiscount ? `
            <div class="lsg-buybox__price-discount text-primary-dark font-bold bg-secondary-yellow rounded-full px-2 py-1">
              ${discountPct}% OFF
            </div>
          ` : ''}
        </div>
      `;
    }

    const imageEl = document.getElementById('pdp-sticky-bar-image');
    if (imageEl && variant.featured_image) {
      const src = variant.featured_image.src || variant.featured_image;
      if (src) {
        imageEl.src = src.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, '_80x80.$1');
        imageEl.alt = variant.title || imageEl.alt;
      }
    }

    const btn = document.getElementById('pdp-sticky-bar-add-to-cart');
    if (btn) {
      const available = variant.available;
      btn.disabled = !available;
      btn.textContent = available ? 'Add to Cart' : 'Out of Stock';
      btn.classList.toggle('disabled', !available);
      btn.classList.toggle('opacity-50', !available);
      btn.classList.toggle('cursor-not-allowed', !available);
      btn.classList.toggle('cursor-pointer', available);
    }
  }

  updateStickyBarFromVariantInput(variantId) {
    const input = document.querySelector(`input[name="variant"][value="${variantId}"]`);
    if (!input) return;

    const label = input.closest('label');
    if (!label) return;

    const variantNameEls = this.querySelectorAll('#pdp-sticky-bar-variant-name');
    const titleEl = label.querySelector('h3');
    if (titleEl) {
      variantNameEls.forEach((el) => {
        const isMobile = el.textContent.trimStart().startsWith('-');
        el.textContent = isMobile ? ` - ${titleEl.textContent}` : titleEl.textContent;
      });
    }

    const priceEl = document.getElementById('pdp-sticky-bar-price');
    const priceSpan = label.querySelector('span');
    if (priceEl && priceSpan) {
      priceEl.textContent = priceSpan.textContent;
    }

    const imageEl = document.getElementById('pdp-sticky-bar-image');
    const variantImg = label.querySelector('img');
    if (imageEl && variantImg) {
      imageEl.src = variantImg.src;
    }
  }
}

customElements.define('pdp-sticky-bar', PDPStickyBar);
